import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { z } from 'zod';
import { apiSuccessSchema, type PagedResult } from '@/shared/types/api';
import { toApiError } from './api-error';

const TOKEN_STORAGE_KEY = 'msc.jwt';
const CORRELATION_HEADER = 'X-Correlation-Id';

/** Le o token da sessao. Fica isolado aqui para haver um unico ponto de leitura. */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Navegador com armazenamento bloqueado: a sessao vale so para esta aba.
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Sem armazenamento, o token vive apenas na memoria do cliente Axios.
  }
}

/** Notificado quando a API recusa o token, para a aplicacao encerrar a sessao. */
type UnauthenticatedHandler = () => void;
let onUnauthenticated: UnauthenticatedHandler = () => {};

export function setUnauthenticatedHandler(handler: UnauthenticatedHandler): void {
  onUnauthenticated = handler;
}

function createClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api/v1',
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config) => {
    const token = getToken();
    // O token vai no header, nunca na query string: URL entra em log de servidor,
    // historico do navegador e cabecalho Referer — foi o que o legado fazia.
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status ?? null;
      const apiError = toApiError(error, status, axiosError.response?.data);

      if (apiError.isUnauthenticated) {
        setToken(null);
        onUnauthenticated();
      }

      return Promise.reject(apiError);
    },
  );

  return instance;
}

export const http = createClient();

/**
 * Faz a requisicao e **valida a resposta contra o schema**.
 *
 * A validacao na fronteira e deliberada: se o backend mudar o contrato, o erro
 * aparece aqui, com o campo exato, em vez de virar `undefined` tres telas adiante.
 */
async function request<T>(schema: z.ZodType<T>, config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<unknown>(config);
  const parsed = apiSuccessSchema(schema).safeParse(response.data);

  if (!parsed.success) {
    throw toApiError(parsed.error, response.status, {
      success: false,
      error: {
        code: 'CONTRACT_MISMATCH',
        message: 'A resposta do servidor não corresponde ao formato esperado.',
        correlationId: response.headers[CORRELATION_HEADER.toLowerCase()] as string | undefined,
      },
    });
  }

  return parsed.data.data;
}

/** Busca um recurso. */
export function get<T>(schema: z.ZodType<T>, url: string, params?: unknown, signal?: AbortSignal): Promise<T> {
  return request(schema, { method: 'GET', url, params, signal });
}

/** Busca uma listagem paginada, devolvendo itens e metadados juntos. */
export async function getPaged<T>(
  itemSchema: z.ZodType<T>,
  url: string,
  params?: unknown,
): Promise<PagedResult<T>> {
  const response = await http.request<unknown>({ method: 'GET', url, params });
  const parsed = apiSuccessSchema(itemSchema.array()).safeParse(response.data);

  if (!parsed.success || !parsed.data.meta) {
    throw toApiError(parsed.success ? new Error('meta ausente') : parsed.error, response.status, {
      success: false,
      error: {
        code: 'CONTRACT_MISMATCH',
        message: 'A resposta do servidor não corresponde ao formato esperado.',
      },
    });
  }

  return { items: parsed.data.data, meta: parsed.data.meta };
}

export function post<T>(schema: z.ZodType<T>, url: string, data?: unknown): Promise<T> {
  return request(schema, { method: 'POST', url, data });
}

/**
 * Envia um arquivo junto de campos de formulário.
 *
 * O `Content-Type` sai explicitamente do cabeçalho para o navegador montá-lo com
 * o `boundary` do multipart — sem isso o servidor recebe um corpo que não sabe
 * separar. O tempo limite é maior porque a transcrição de um atendimento
 * inteiro leva mais que os 30 segundos padrão.
 */
export function postMultipart<T>(
  schema: z.ZodType<T>,
  url: string,
  data: FormData,
  timeout = 180_000,
): Promise<T> {
  return request(schema, {
    method: 'POST',
    url,
    data,
    timeout,
    headers: { 'Content-Type': undefined },
  });
}

export function put<T>(schema: z.ZodType<T>, url: string, data?: unknown): Promise<T> {
  return request(schema, { method: 'PUT', url, data });
}

export function patch<T>(schema: z.ZodType<T>, url: string, data?: unknown): Promise<T> {
  return request(schema, { method: 'PATCH', url, data });
}

/** Exclui um recurso. A API responde `204`, entao nao ha corpo a validar. */
export async function del(url: string): Promise<void> {
  await http.request({ method: 'DELETE', url });
}

/**
 * Envia uma operacao que responde `204`.
 *
 * Sem isto, `post()` tentaria validar um envelope que nao existe e transformaria
 * um sucesso em `CONTRACT_MISMATCH`.
 */
export async function postNoContent(url: string, data?: unknown): Promise<void> {
  await http.request({ method: 'POST', url, data });
}
